import { createFormDataDocuments } from '_utils/helpers'
import humps from 'humps'

import { getToken } from '_utils/token'

import { get, patch, post, del, blobInstance } from './requests'

export const getBudget = (key, serviceOrderId, budgetId) =>
  get(['serviceorders', serviceOrderId, 'budgets', budgetId], { key })

export const getBudgetById = (key, budgetId, params) =>
  get(['budgets', budgetId], { key, params: humps.decamelizeKeys(params) })

// Gera o PDF do orçamento no backend (mesmo motor usado no pacote de anexos e
// no envio por WhatsApp) e retorna a resposta do axios (blob em response.data).
export const downloadBudgetPdf = budgetId => {
  const headers = getToken()
  return blobInstance.get(`/budgets/${budgetId}/download-pdf/`, {
    ...headers,
  })
}

export const deleteBudget = (key, serviceOrderId, budgetId) =>
  del(['serviceorders', serviceOrderId, 'budgets', budgetId, 'delete-budget'], { key })

export const getBudgetByServiceOrder = (key, serviceOrderId) =>
  get(['serviceorders', serviceOrderId, 'search-tradesman-budget'], { key })

export const createOpenProposeBudget = (key, serviceOrderId, payload) =>
  post(['serviceorders', serviceOrderId, 'open-propose-budget'], { key }, payload)

export const formDataHeader = {
  'Content-Type': 'multipart/form-data; charset=UTF-8',
}

export const updateBudget = (key, serviceOrderId, budgetId, payload, options = {}) =>
  patch(['serviceorders', serviceOrderId, 'budgets', budgetId], { key, ...options }, payload)

// use_agency_terms: quando true, o backend substitui os rótulos
// de 'owner' e 'requester' pelos termos customizados da agência.
// Mantive true por padrão para UX consistente; pode ser desativado
// conforme necessidade de cenários específicos.
export const getPayerOptions = (key, serviceOrderId, budgetId, { useAgencyTerms = true } = {}) =>
  get(['serviceorders', serviceOrderId, 'budgets', budgetId, 'get-options'], {
    key,
    params: { use_agency_terms: useAgencyTerms },
    transformOnlyResponse: true,
  })

export const deleteBudgetItem = (key, serviceOrderId, budgetId, itemId) =>
  del(['serviceorders', serviceOrderId, 'budgets', budgetId, 'budgetitems', itemId], { key })

export const updateBudgetItem = (key, serviceOrderId, budgetId, itemId, payload) =>
  patch(
    ['serviceorders', serviceOrderId, 'budgets', budgetId, 'budgetitems', itemId],
    { key },
    payload
  )

export const deleteBudgetPicture = (key, serviceOrderId, budgetId, pictureId) =>
  del(['serviceorders', serviceOrderId, 'budgets', budgetId, 'budgetpictures', pictureId], { key })

export const deleteBudgetPictures = (key, serviceOrderId, budgetId, payload) =>
  patch(
    ['serviceorders', serviceOrderId, 'budgets', budgetId, 'delete-budget-pictures'],
    {
      key,
    },
    payload
  )

export const addBudgetPicture = (key, serviceOrderId, budgetId, payload) => {
  const newPayload = { budget: budgetId, picture: payload }
  return post(
    ['serviceorders', serviceOrderId, 'budgets', budgetId, 'budgetpictures'],
    { key, transformPayload: false, formData: true },
    createFormDataDocuments(newPayload)
  )
}

export const importAttachments = (key, serviceOrderId, budgetId) =>
  post(['serviceorders', serviceOrderId, 'budgets', budgetId, 'import-attachments'], {
    key,
  })

export const refuseBudget = (key, serviceOrderId, budgetId, payload) =>
  patch(['serviceorders', serviceOrderId, 'budgets', budgetId, 'refuse'], { key }, payload)

export const clientRefuseBudget = (key, serviceOrderId, budgetId) =>
  post(['serviceorders', serviceOrderId, 'budgets', budgetId, 'person-paying-refuse-budget'], {
    key,
  })

export const unrefuseBudget = (key, serviceOrderId, budgetId) =>
  post(['serviceorders', serviceOrderId, 'budgets', budgetId, 'unrefuse'], { key })

export const sendBudgetCounterProposal = (key, serviceOrderId, budgetId, payload) =>
  patch(
    ['serviceorders', serviceOrderId, 'budgets', budgetId, 'counter-proposal'],
    { key },
    payload
  )

export const approvePicturesBudget = (key, serviceOrderId, budgetId, payload) =>
  patch(
    ['serviceorders', serviceOrderId, 'budgets', budgetId, 'approve-pictures'],
    { key, transformPayload: false, formData: true },
    createFormDataDocuments(payload, true)
  )

export const approveBudget = (key, serviceOrderId, budgetId, payload) => {
  const endpoint = ['serviceorders', serviceOrderId, 'budgets', budgetId, 'approve']
  const options = { key, transformPayload: false, formData: true }

  // NOTE: Only inner budgets (budget.proposeOpened = false) shouldn't have a payload
  if (!payload) {
    return patch(endpoint, options)
  }

  return patch(endpoint, options, createFormDataDocuments(payload, true))
}

export const generateBudgetPaymentLink = (key, serviceOrderId, budgetId, payload = {}) =>
  post(
    ['serviceorders', serviceOrderId, 'budgets', budgetId, 'payment-link-generate'],
    { key, transformPayload: true },
    payload
  )

export const saveManualBudgetPaymentLink = (key, serviceOrderId, budgetId, payload) =>
  post(
    ['serviceorders', serviceOrderId, 'budgets', budgetId, 'payment-link-manual'],
    { key, transformPayload: true },
    payload
  )

export const clientApproveBudget = (key, serviceOrderId, budgetId, payload) =>
  post(
    ['serviceorders', serviceOrderId, 'budgets', budgetId, 'person-paying-approve-budget'],
    { key, transformPayload: true },
    payload
  )

export const cancelApproveBudget = (key, serviceOrderId, budgetId) =>
  post(['serviceorders', serviceOrderId, 'budgets', budgetId, 'cancel-approval'], {
    key,
    transformPayload: false,
  })

export const sendBudgetEmail = (key, serviceOrderId, budgetId) =>
  post(['serviceorders', serviceOrderId, 'budgets', budgetId, 'send-email-payment-user'], { key })

export const sendBudgetWhatsapp = (key, serviceOrderId, budgetId) =>
  post(['serviceorders', serviceOrderId, 'budgets', budgetId, 'send-wpp-payer'], { key })

export const approveCustomerBudget = (key, serviceOrderId, budgetId) =>
  post(['budgets', budgetId, 'accept-proposal'], {
    key,
    transformPayload: false,
  })

export const refuseCustomerBudget = (key, serviceOrderId, budgetId) =>
  post(['budgets', budgetId, 'refuse-proposal'], {
    key,
    transformPayload: false,
  })

export const createPropose = (key, serviceOrderId, budgetId) =>
  post(['serviceorders', serviceOrderId, 'budgets', budgetId, 'create-propose'], { key })

export const returnBudgetProvider = (key, serviceOrderId, budgetId, payload) =>
  post(
    ['serviceorders', serviceOrderId, 'budgets', budgetId, 'return-budget-provider'],
    { key, transformPayload: false },
    createFormDataDocuments(payload, true)
  )

export const sendWhatsapp = (key, serviceOrderId, budgetId) =>
  post(['serviceorders', serviceOrderId, 'budgets', budgetId, 'send-whatsapp'], { key })

// TODO: Move everything related to step_status into service-order folders (services and modules)
export const getStepStatusLog = (key, serviceOrderId) =>
  get(['serviceorders', serviceOrderId, 'step-status-log'], { key })

export const getEditExecutionInfo = (key, serviceOrderId, budgetId) =>
  get(['serviceorders', serviceOrderId, 'budgets', budgetId, 'edit-execution-screen-info'], {
    key,
  })

export const verifyPaidInstallment = (key, serviceOrderId, budgetId) =>
  get(['serviceorders', serviceOrderId, 'budgets', budgetId, 'verify-paid-installment'], { key })

export const fetchPaidInstallments = (key, budgetId) =>
  get(['budgets', budgetId, 'paid-installments'], { key })

export const fetchBudgetServices = (key, params) =>
  get(['budget-services'], { key, params: humps.decamelizeKeys(params), transformPayload: true })

export const postBudgetService = (key, payload) =>
  post(['budget-services'], { key, transformPayload: true }, payload)

export const patchBudgetService = (key, serviceId, payload) =>
  patch(['budget-services', serviceId], { key, transformPayload: true }, payload)

export const deleteBudgetService = (key, serviceId) => del(['budget-services', serviceId], { key })

export const fetchBudgetImportSuggestions = (key, budgetId, payload) => {
  return post(['budgets', budgetId, 'import-suggestions'], { key }, payload)
}
export const fetchBudgetUndoImportSuggestions = (key, budgetId, payload) =>
  post(['budgets', budgetId, 'undo-import-suggestions'], { key }, payload)

export const fetchBudgetAdjustServices = (key, budgetId, payload) =>
  post(['budgets', budgetId, 'budget-adjust-services'], { key }, payload)

export const getReasonsToRefusal = key => get(['budget-refusal-reasons'], { key })

export const createBudgetTemplate = key => (budgetId, payload) =>
  post(
    ['budgets', budgetId, 'create-budget-template'],
    {
      key,
      transformPayload: true,
    },
    payload
  )

export const postBudgetSaveTemplates = (key, budgetId, payload) =>
  post(['budgets', budgetId, 'budget-save-templates'], { key }, payload)

export const patchBudgetSaveTemplates = (key, budgetId, payload, params) =>
  patch(
    ['budgets', budgetId, 'edit-budget-save-templates'],
    { key, params: humps.decamelizeKeys(params), transformPayload: true },
    payload
  )

export const deleteBudgetSaveTemplates = (key, budgetId, params) =>
  del(['budgets', budgetId, 'delete-budget-save-templates'], {
    key,
    params: humps.decamelizeKeys(params),
    transformPayload: true,
  })

export const getBudgetSaveTemplates = (key, budgetId) =>
  get(['budgets', budgetId, 'budget-save-templates'], { key })

export const getBudgetLoadTemplates = key => get(['budget-template-service'], { key })

export const postBudgetLoadTemplates = (key, budgetId, payload) =>
  post(['budgets', budgetId, 'budget-load-templates'], { key }, payload)

export const getVerifyBudgetFinancialValues = (key, budgetId) =>
  get(['budgets', budgetId, 'verify-budget-financial-values'], { key })

export const getBudgetStatus = (key, budgetId) =>
  get(['budgets', budgetId, 'budget-status'], { key })

export const createCoverageRule = (key, payload) => post(['coverage-rules'], { key }, payload)

export const getCoverageRuleVersion = (key, params = {}) =>
  get(['coverage-rules', 'get-version'], { key, params })

export const updateBudgetCoverage = (key, budgetId, payload) =>
  patch(['coverage-rules', budgetId, 'update-budget-coverage'], { key }, payload)

export const saveBudgetServices = (key, budgetId, payload) =>
  post(['budgets', budgetId, 'save-budget-services'], { key }, payload)

export const getServicesToImport = (key, params) =>
  get(['budget-resolution', 'get-services-to-import'], {
    key,
    params: humps.decamelizeKeys(params),
    transformPayload: true,
  })

export const deleteBudgetResolution = (key, budgetResolutionId) =>
  del(['budget-resolution', budgetResolutionId], { key })

export const updateBudgetResolution = (key, budgetId, payload) =>
  patch(
    ['budget-resolution', budgetId],
    {
      key,
      transformOnlyResponse: true,
      formData: true,
    },
    payload
  )

export const createBudget = (key, payload) =>
  post(
    ['budgets'],
    {
      key,
      transformPayload: true,
      transformFormData: true,
      formData: true,
    },
    payload
  )

export const deleteUnsavedBudgetServices = (key, budgetId, payload) =>
  post(['budgets', budgetId, 'delete-unsaved-budget-services'], { key }, payload)

export const getAiValidation = (key, payload) =>
  post(['problem', 'get-ai-validation'], { key, transformPayload: true }, payload)

export const getAiResponse = (key, payload) =>
  post(['ai-model', 'ai-response'], { key, transformPayload: true }, payload)

export const prepareBudgetResolution = (key, budgetId) =>
  post(['budgets', budgetId, 'prepare-budget-resolution'], { key })

export const importBudgetTemplate = (key, serviceOrderId, payload) =>
  post(
    ['serviceorders', serviceOrderId, 'import-budget-template'],
    { key, transformPayload: true },
    payload
  )

export const postLogData = (key, payload) =>
  post(['problem', 'post-log-data'], { key, transformPayload: true }, payload)

export const createAdditionalBudgets = (key, serviceOrderId, budgetId, payload) =>
  post(
    ['serviceorders', serviceOrderId, 'budgets', budgetId, 'create-additional-budgets'],
    { key, transformPayload: true },
    payload
  )

export const updateResolutionAiSuggestion = (key, resolutionId, payload) =>
  patch(
    ['budget-resolution', resolutionId, 'update-ai-suggestions'],
    {
      key,
      transformOnlyResponse: true,
      formData: true,
    },
    payload
  )
